import { FC } from "react";
import {
  Button,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Badge,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";

interface Agent {
  id: string;
  name: string;
  description: string;
  isLocal: boolean;
}

interface AgentSelectorProps {
  agents: Agent[];
  selectedAgents: string[];
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

export const AgentSelector: FC<AgentSelectorProps> = ({
  agents,
  selectedAgents,
  onToggle,
  onSelectAll,
  onDeselectAll,
}) => {
  return (
    <Card>
      <CardHeader>
        <Stack direction="row" justify="between" align="center">
          <Typography size="regular" weight="medium">
            Agents ({selectedAgents.length}/{agents.length})
          </Typography>
          <Stack direction="row" gap="xs">
            <Button onClick={onSelectAll} variant="ghost" size="sm" className="al-text-xs">
              Select All
            </Button>
            <Button onClick={onDeselectAll} variant="ghost" size="sm" className="al-text-xs">
              Clear
            </Button>
          </Stack>
        </Stack>
      </CardHeader>
      <CardContent className="al-max-h-96 al-overflow-y-auto">
        {agents.length === 0 ? (
          <div className="al-text-center al-py-8 al-text-sm al-text-gray-500">
            No agents available. Create agents in the Studio Agents tab.
          </div>
        ) : (
          <div className="al-space-y-2">
            {agents.map(agent => (
              <div
                key={agent.id}
                role="button"
                tabIndex={0}
                className={`al-p-3 al-border al-rounded-md al-cursor-pointer al-transition-colors ${
                  selectedAgents.includes(agent.id)
                    ? "al-bg-blue-50 al-border-blue-300"
                    : "al-bg-white al-border-gray-200 hover:al-border-gray-300"
                }`}
                onClick={() => onToggle(agent.id)}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onToggle(agent.id);
                  }
                }}
              >
                <Stack direction="row" align="start" gap="sm">
                  <Checkbox
                    checked={selectedAgents.includes(agent.id)}
                    onCheckedChange={() => onToggle(agent.id)}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    className="al-mt-0.5"
                  />
                  <div className="al-flex-1">
                    <Stack direction="row" align="center" gap="xs" className="al-mb-1">
                      <Typography size="sm" weight="medium">
                        {agent.name}
                      </Typography>
                      {agent.isLocal && (
                        <Badge className="!al-bg-blue-100 !al-text-blue-700 al-text-xs">
                          Local
                        </Badge>
                      )}
                    </Stack>
                    <Typography size="xs" className="al-text-gray-600 al-line-clamp-2">
                      {agent.description}
                    </Typography>
                  </div>
                </Stack>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
